import { useState } from "react";
import { NavLink, Outlet, Link } from "react-router-dom";
import { TutorialInvite } from "@/components/TutorialInvite";
import { DemoBanner } from "@/components/DemoBanner";
import { BarChart3, ClipboardList, Package, CircleUser, Landmark, ChefHat, Store, ChevronDown, CheckCircle2, LogOut, Lock } from "lucide-react";
import { useAuth, ROLE_INFO } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { useLayout } from "@/context/LayoutContext";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

function useTabs() {
  const { isMgmt } = useAuth();
  return TABS.filter((t) => !t.mgmt || isMgmt);
}

const TABS = [
  { to: "/", label: "Painel", icon: BarChart3, end: true, id: "dashboard" },
  { to: "/conferences", label: "Conferências", short: "Conferir", icon: ClipboardList, id: "conferences" },
  { to: "/products", label: "Produtos", icon: Package, id: "products" },
  { to: "/recipes", label: "Fichas técnicas", short: "Fichas", icon: ChefHat, id: "recipes" },
  { to: "/dre", label: "DRE", icon: Landmark, id: "dre", mgmt: true },
  { to: "/menu", label: "Menu", icon: CircleUser, id: "menu" },
];

export function UnitSwitcher({ compact }) {
  const { unit, units, selectUnit } = useUnit();
  const [open, setOpen] = useState(false);
  if (!unit) return null;
  return (
    <>
      <button
        className="chip"
        style={{ maxWidth: compact ? "min(160px, 42vw)" : "100%" }}
        disabled={units.length < 2}
        onClick={() => setOpen(true)}
        data-testid="unit-switcher-button"
      >
        <Store size={15} />
        <span className="truncate">{unit.name}</span>
        {units.length > 1 && <ChevronDown size={14} />}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--surface)] text-[var(--text)] border-[var(--border)] rounded-2xl" data-testid="unit-picker-sheet">
          <DialogTitle className="h2">Escolha a unidade</DialogTitle>
          <div className="flex flex-col gap-2">
            {units.map((u) => (
              <button
                key={u.id}
                className="card flex items-center justify-between text-left"
                style={{ borderColor: u.id === unit.id ? "var(--brand)" : undefined, padding: 14 }}
                onClick={() => { selectUnit(u.id); setOpen(false); }}
                data-testid={`unit-option-${u.id}`}
              >
                <span className="h3">{u.name}</span>
                {u.id === unit.id && <CheckCircle2 size={18} style={{ color: "var(--brand)" }} />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Brand() {
  const { layout } = useLayout();
  return (
    <div className="flex items-center gap-3 min-w-0">
      {layout?.logo_url ? (
        <img src={layout.logo_url} alt="" className="w-9 h-9 rounded-xl object-cover" />
      ) : (
        <div className="w-9 h-9 rounded-xl flex items-center justify-center text-white font-extrabold" style={{ background: "var(--brand)" }}>
          {(layout?.app_name || "C")[0]}
        </div>
      )}
      <div className="min-w-0">
        <div className="h3 truncate" data-testid="app-brand-name">{layout?.app_name}</div>
        <div className="caption truncate">Controle de CMV</div>
      </div>
    </div>
  );
}

function Sidebar() {
  const { user, logout } = useAuth();
  return (
    <aside className="hidden lg:flex flex-col w-64 shrink-0 h-screen sticky top-0 p-5 gap-6 border-r no-print" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
      <Brand />
      <UnitSwitcher />
      <nav className="flex flex-col gap-1">
        {useTabs().map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid={`nav-${t.id}`}>
            <t.icon size={19} /> {t.label}
          </NavLink>
        ))}
      </nav>
      <div className="mt-auto card card-soft flex items-center gap-3" style={{ padding: 12 }}>
        <div className="w-9 h-9 rounded-full flex items-center justify-center font-bold text-white" style={{ background: "var(--brand)" }}>{user?.name?.[0]}</div>
        <div className="flex-1 min-w-0">
          <div className="label truncate">{user?.name}</div>
          <div className="caption">{ROLE_INFO[user?.role]?.label}</div>
        </div>
        <button className="icon-btn" onClick={logout} title="Sair" data-testid="sidebar-logout-button"><LogOut size={16} /></button>
      </div>
    </aside>
  );
}

function TopNav() {
  return (
    <header className="hidden lg:flex items-center gap-6 px-8 h-16 border-b sticky top-0 z-20 no-print" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
      <Brand />
      <nav className="flex gap-1 ml-4">
        {useTabs().map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`} data-testid={`nav-${t.id}`}>
            <t.icon size={18} /> {t.label}
          </NavLink>
        ))}
      </nav>
      <div className="ml-auto"><UnitSwitcher compact /></div>
    </header>
  );
}

function MobileBar() {
  return (
    <>
      <header className="lg:hidden flex items-center justify-between gap-3 px-4 h-14 border-b sticky top-0 z-20 no-print overflow-hidden" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <Brand />
        <UnitSwitcher compact />
      </header>
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 grid border-t no-print" style={{ background: "var(--surface)", borderColor: "var(--border)", paddingBottom: "env(safe-area-inset-bottom)", gridTemplateColumns: `repeat(${useTabs().length}, minmax(0, 1fr))` }}>
        {useTabs().map((t) => (
          <NavLink key={t.to} to={t.to} end={t.end} data-testid={`tab-${t.id}`}
            className="flex flex-col items-center gap-1 py-2.5 text-[11px] font-semibold"
            style={({ isActive }) => ({ color: isActive ? "var(--brand)" : "var(--muted)" })}>
            <t.icon size={21} /> <span className="truncate max-w-full px-0.5">{t.short || t.label}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}

function ReadOnlyBanner() {
  const { user, isAdmin } = useAuth();
  const t = user?.tenant;
  if (t?.state !== "vencido") return null;
  const trial = t.status === "teste";
  return (
    <div className="flex flex-wrap items-center gap-3 px-4 py-3 no-print" style={{ background: "#FBEFD9", color: "#6B4A0E", borderBottom: "1px solid #EBD3A3" }} data-testid="readonly-banner">
      <Lock size={16} />
      <span className="flex-1 min-w-[200px] text-sm font-semibold">{trial ? "Seu teste grátis terminou." : "Sua assinatura venceu."} Você pode consultar seus dados, mas cadastrar e editar está bloqueado{isAdmin ? (trial ? " até assinar." : " até pagar a mensalidade.") : " · fale com o administrador do restaurante."}</span>
      {isAdmin && <Link to="/billing" className="btn btn-primary btn-sm" data-testid="readonly-subscribe-button">{trial ? "Assinar agora" : "Pagar mensalidade"}</Link>}
    </div>
  );
}

export default function Shell() {
  const { layout } = useLayout();
  const top = layout?.nav === "topbar";
  return (
    <div className={`min-h-screen ${top ? "" : "lg:flex"}`}>
      {top ? <TopNav /> : <Sidebar />}
      <MobileBar />
      <main className="flex-1 min-w-0"><DemoBanner /><ReadOnlyBanner /><Outlet /></main>
      <TutorialInvite />
    </div>
  );
}
